import useMarkOnMount from 'crm-fe-perf/timing/hooks/useMarkOnMount';
import { RecordRouteMounted } from 'crm-fe-perf/timing/marks/crm-records-ui/performanceMarks';
import { ObjectTypesToIds, normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { CONTACT } from 'customer-data-objects/constants/ObjectTypes';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { useEffect, useMemo } from 'react';
import { useRouteMatch, useHistory, useLocation, useParams } from 'react-router-dom';
import ProfileContainer from '../crm_ui/profile/ProfileContainer';
import { useBodyClass } from '../router/useBodyClass';
import { useQueryParams } from '../router/useQueryParams';
import { RouterMetrics } from '../router/RouterMetrics';
import { useRedirectOnObjectCreation } from '../hooks/useRedirectOnObjectCreation';
import { useRedirectOnObjectDeletion } from '../hooks/useRedirectOnObjectDeletion';
import { useAllPropertiesPanelDeepLink } from '../hooks/useAllPropertiesPanelDeepLink';
import { View } from 'foundations-components/components/View';
import { useLazyQuery } from 'data-fetching-client';
import { delayUntilIdle } from '../crm_ui/utils/delayUntilIdle';
import { LOG_CRM_OBJECT_USER_VIEW_QUERY } from '../../__generated__/chirp/com/hubspot/crm/object/user/views/rpc/CrmObjectUserViewsRpc.dfc';
import { useEngagementDeepLink } from '../hooks/useEngagementDeepLink';
// regex for matching extra path params appended to URLs. in the old contact record we
// supported deep linking to parts of the record, but not anymore, so we need to strip
// out those paths.
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const oldContactRecordRegex = /^(\/contacts?\/\d+\/)[A-Za-z-]+$/;
export function _useRedirectEngagementDeepLinks() {
  const history = useHistory();
  const {
    path,
    url
  } = useRouteMatch();
  useEffect(() => {
    if (!path.includes('/view/:tabId') && history.location.search.includes('engagement=')) {
      RouterMetrics.counter('engagement-deep-link-redirects').increment();
      const urlWithTrailingSlash = url.endsWith('/') ? url : `${url}/`;
      history.replace(`${urlWithTrailingSlash}view/1${history.location.search}`);
    }
  }, [path, url, history, history.location.search]);
}
let isFirstLoad = true;
export default function RecordRoute({
  subjectId: subjectIdOverride,
  objectType: objectTypeOverride
}) {
  const {
    objectType: objectTypePathParam,
    subjectId: subjectIdPathParam
  } = useParams();
  const objectType = objectTypeOverride || objectTypePathParam;
  const objectTypeId = normalizeTypeId(objectType);
  const subjectId = subjectIdPathParam || subjectIdOverride || '-';

  // TODO: Remove when BE ports this to the BE https://git.hubteam.com/HubSpot/crm-records/issues/1392
  const [logCrmObjectUserView] = useLazyQuery(LOG_CRM_OBJECT_USER_VIEW_QUERY);
  _useRedirectEngagementDeepLinks();
  useAllPropertiesPanelDeepLink(subjectId, objectTypeId);
  useEngagementDeepLink(subjectId, objectTypeId);
  const confirmNavigationModal = useRedirectOnObjectCreation();
  useRedirectOnObjectDeletion({
    subjectId,
    objectType
  });
  const query = useQueryParams();
  const history = useHistory();
  const {
    pathname,
    search
  } = useLocation();
  const recordRouteMountedDimensions = useMemo(() => {
    return {
      is_first_load: String(isFirstLoad),
      object_type_id: String(objectTypeId)
    };
    // memoize the dimensions to avoid re-marking, we only want to mark on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useMarkOnMount(RecordRouteMounted, recordRouteMountedDimensions);
  isFirstLoad = false;
  useBodyClass(objectType);
  useEffect(() => {
    if (objectType === CONTACT && oldContactRecordRegex.test(pathname)) {
      // if a user is linked to an old route that has extra path parameters,
      // strip that param off.
      const [__, capturedUrl] = pathname.match(oldContactRecordRegex);
      history.replace(`${capturedUrl}${search}`);
    }
  }, [history, objectType, pathname, search, subjectId]);
  useEffect(() => {
    CrmLogger.log('recordPageView', {
      type: ObjectTypesToIds[objectType] || objectType,
      screen: ObjectTypesToIds[objectType] || objectType,
      subscreen: 'profile',
      subjectId
    });
    delayUntilIdle(() => {
      logCrmObjectUserView({
        variables: {
          objectTypeId,
          objectId: parseInt(subjectId, 10),
          interactionType: 'VIEW',
          namespace: 'CRM_OBJECTS'
        }
      });
    });

    // add subjectId to the dependency array even though it doesn't need to be
    // there to force the tracking to fire for every record
  }, [objectType, subjectId, logCrmObjectUserView, objectTypeId]);
  return /*#__PURE__*/_jsxs(View, {
    blockSize: "100%",
    "data-selenium-test": "universal-record",
    children: [/*#__PURE__*/_jsx(ProfileContainer, {
      objectType: objectType,
      subjectId: subjectId,
      query: query
    }), confirmNavigationModal]
  });
}